import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { translate } from 'react-i18next';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import { contractType } from '../../../common/enums';
import { selectToken } from '../../../features/auth/ducks';
import { showModal } from '../../../features/modal/ducks';
import { signPostFinancingActions } from '../ducks';
import { apiCallIds } from '../api';
import { getFileUrl } from '../../../common/utils/helpers';
import { selectIsInProgress } from '../../../features/spinner/ducks';
import { OpenFileLink } from '../../../common/components/data-display';

const SIGNING_PHASE_SIGN = 'sign';
const SIGNING_PHASE_CONFIRM = 'confirm';

const mapStateToProps = state => ({
  token: selectToken(state),
  isSignLoading: selectIsInProgress(state, apiCallIds.SIGN_POST_FINANCING),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      signPostFinancing: signPostFinancingActions.request,
      showConfirmPostFinancingModal: loanId => showModal({
        id: apiCallIds.CONFIRM_POST_FINANCING,
        data: loanId,
      }),
    },
    dispatch
  ),
});

@translate()
@connect(mapStateToProps, mapDispatchToProps)
export default class SignPostFinancingBlock extends Component {
  static propTypes = {
    loan: PropTypes.object.isRequired,
    initialPhase: PropTypes.string,
    token: PropTypes.string.isRequired,
    isSignLoading: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
  };

  state = {
    checkedContracts: {},
    phase: this.props.initialPhase || SIGNING_PHASE_SIGN,
    signError: null,
  };

  handleSign = () => {
    const { actions, loan } = this.props;
    actions.signPostFinancing({
      loanId: loan.id,
      onSuccess: () => this.setState({ phase: SIGNING_PHASE_CONFIRM, signError: null }),
      onError: (error) => this.setState({ signError: error || true }),
    });
  };

  onContractChecked = contractKey => (event) => {
    this.setState({
      checkedContracts: {
        ...this.state.checkedContracts,
        [contractKey]: event.target.checked,
      },
    });
  };

  showConfirmModal = () => {
    const { actions, loan } = this.props;
    actions.showConfirmPostFinancingModal(loan.id);
  };

  getPostFinancingContracts() {
    const { loan } = this.props;
    if (!loan.contracts) return [];

    const postFinancingTypes = [
      contractType.LOAN,
      contractType.DEDUCTIONS_AGREEMENT,
      contractType.INSURANCE_CONTRACT,
    ];
    return loan.contracts.filter(c => postFinancingTypes.includes(c.type));
  }

  getContractLabel(contract) {
    const { t } = this.props;
    const labels = {
      [contractType.LOAN]: t('Zmluva o pôžičke'),
      [contractType.DEDUCTIONS_AGREEMENT]: t('Dohoda o zrážkach zo mzdy a z iných príjmov'),
      [contractType.INSURANCE_CONTRACT]: t('Zmluva o zabezpečení poistenia schopnosti splácať pôžičku'),
    };
    return labels[contract.type] || t('Zmluva');
  }

  getContractKey(contract) {
    return contract.id || contract.type;
  }

  areAllContractsChecked(contracts) {
    return contracts.length > 0 &&
      contracts.every(contract =>
        contract.fileId && this.state.checkedContracts[this.getContractKey(contract)]
      );
  }

  renderContractCheckboxes(contracts) {
    const { token, t } = this.props;
    const readableContracts = contracts.filter(contract => contract.fileId);

    if (!readableContracts.length) {
      return null;
    }

    return (
      <div style={{ margin: '8px 0 12px' }}>
        <p style={{ marginBottom: 6 }}>{t('Dokumenty na podpis:')}</p>
        <ul style={{ paddingLeft: 0, marginBottom: 0, listStyle: 'none' }}>
          {readableContracts.map(contract => (
            <li key={this.getContractKey(contract)} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={!!this.state.checkedContracts[this.getContractKey(contract)]}
                onChange={this.onContractChecked(this.getContractKey(contract))}
              >
                {t('Súhlasím so znením dokumentu')}{' '}
                <OpenFileLink href={getFileUrl(contract.fileId, token)}>
                  {this.getContractLabel(contract)}
                </OpenFileLink>
              </Checkbox>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderSignPhase() {
    const { isSignLoading, t } = this.props;
    const { signError } = this.state;

    const contracts = this.getPostFinancingContracts();
    const hasReadableContracts = contracts.some(contract => contract.fileId);
    const hasMissingContractFile = contracts.some(contract => !contract.fileId);
    const allContractsChecked = this.areAllContractsChecked(contracts);

    return (
      <div>
        {hasReadableContracts && this.renderContractCheckboxes(contracts)}
        {!hasReadableContracts && (
          <p style={{ color: 'red' }}>
            {t('Dokumenty na podpis nie je možné načítať. Kontaktujte podporu.')}
          </p>
        )}
        {hasMissingContractFile && hasReadableContracts && (
          <p style={{ color: 'red' }}>
            {t('Niektoré dokumenty na podpis nie je možné načítať. Kontaktujte podporu.')}
          </p>
        )}
        {signError && (
          <p style={{ color: 'red' }}>
            {typeof signError === 'string' ? signError : t('Podpis sa nepodaril. Skúste znova.')}
          </p>
        )}
        <div className="ant-modal-footer">
          <Button
            size="small"
            type="primary"
            loading={isSignLoading}
            onClick={this.handleSign}
            disabled={!allContractsChecked}
          >
            {t('Podpísať zmluvy')}
          </Button>
        </div>
      </div>
    );
  }

  renderConfirmPhase() {
    const { t } = this.props;

    return (
      <div>
        <p>{t('SMS kód bol odoslaný. Zadajte ho v modálnom okne pre potvrdenie podpisu zmlúv.')}</p>
        <div className="ant-modal-footer">
          <Button
            size="small"
            type="primary"
            onClick={this.showConfirmModal}
          >
            {t('Zadať SMS kód')}
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { phase } = this.state;

    if (phase === SIGNING_PHASE_CONFIRM) {
      return this.renderConfirmPhase();
    }
    return this.renderSignPhase();
  }
}



// WEBPACK FOOTER //
// ./src/features/loans/components/SignPostFinancingBlock.js